import React, { useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import gsap from "gsap";
import { useTranslation } from "react-i18next";
import { buildWifiPayload } from "../utils/wifiQr";

const QR_TYPES = [
  { id: "link", label: "Link / Text" },
  { id: "wifi", label: "Wi-Fi" },
];

const WIFI_SECURITY = [
  { value: "WPA", label: "WPA/WPA2" },
  { value: "WEP", label: "WEP" },
  { value: "nopass", label: "None" },
];

const INPUT_CLASS = `w-full rounded-2xl border border-ink-200 px-4 py-3 text-base
  focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100 transition-colors`;

const FG_SWATCHES = [
  { name: "Ink", value: "#0f1220" },
  { name: "Brand blue", value: "#2a5ce6" },
  { name: "Accent pink", value: "#d7277d" },
  { name: "Emerald", value: "#059669" },
  { name: "Amber", value: "#d97706" },
  { name: "Violet", value: "#7c3aed" },
  { name: "Teal", value: "#0d9488" },
  { name: "Red", value: "#dc2626" },
];

const BG_SWATCHES = [
  { name: "White", value: "#ffffff" },
  { name: "Soft grey", value: "#f6f7fb" },
  { name: "Cream", value: "#fff7ed" },
  { name: "Pale blue", value: "#eff6ff" },
  { name: "Pale pink", value: "#fdf2f8" },
  { name: "Pale mint", value: "#ecfdf5" },
];

// Logo is drawn at this fraction of the QR width; level H error correction keeps
// the code scannable with the centre covered.
const LOGO_SCALE = 0.24;
const LOGO_W = 1363.11;
const LOGO_H = 744.93;

let logoSvgCache;
async function loadLogoSvg() {
  if (!logoSvgCache) {
    logoSvgCache = fetch(`${process.env.PUBLIC_URL}/images/logo-only.svg`)
      .then((r) => r.text())
      .catch(() => "");
  }
  return logoSvgCache;
}

function getStoredProfileUrl() {
  try {
    const raw = localStorage.getItem("token");
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    const username = parsed?.user?.username;
    return username ? `https://linkakam.com/${username}` : "";
  } catch {
    return "";
  }
}

export default function Qrgen() {
  const { t } = useTranslation();
  const profileUrl = useMemo(getStoredProfileUrl, []);

  const [qrType, setQrType] = useState("link");
  const [text, setText] = useState(profileUrl);
  const [ssid, setSsid] = useState("");
  const [wifiPassword, setWifiPassword] = useState("");
  const [security, setSecurity] = useState("WPA");
  const [hiddenNet, setHiddenNet] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [fg, setFg] = useState(FG_SWATCHES[0].value);
  const [bg, setBg] = useState(BG_SWATCHES[0].value);
  const [useLogo, setUseLogo] = useState(true);
  const [qrPng, setQrPng] = useState("");
  const [qrSvg, setQrSvg] = useState("");
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const canvasRef = useRef(null);
  const revealRef = useRef(null);
  const scanRef = useRef(null);
  const firstRender = useRef(true);

  const playReveal = () => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduceMotion) {
      gsap.set(revealRef.current, { opacity: 1, scale: 1 });
      return;
    }
    const tl = gsap.timeline();
    tl.fromTo(
      revealRef.current,
      { opacity: 0, scale: 0.92 },
      { opacity: 1, scale: 1, duration: 0.45, ease: "power3.out" }
    );
    if (scanRef.current) {
      tl.fromTo(
        scanRef.current,
        { xPercent: -120, opacity: 0.9 },
        { xPercent: 220, opacity: 0, duration: 0.6, ease: "power1.inOut" },
        "<"
      );
    }
  };

  const generate = async (value) => {
    const v = (value ?? "").trim();
    if (!v) {
      setErrMsg(t("Paste a link or type text first"));
      setQrPng("");
      setQrSvg("");
      return;
    }

    setErrMsg("");
    setLoading(true);

    try {
      const canvas = canvasRef.current;
      await QRCode.toCanvas(canvas, v, {
        width: 480,
        margin: 2,
        errorCorrectionLevel: "H",
        color: { dark: fg, light: bg },
      });
      // qrcode's canvas renderer sets inline canvas.style.width/height to match
      // the pixel buffer (480px), which overrides our fixed on-screen size —
      // force it back so the code never renders larger than its placeholder.
      canvas.style.width = "100%";
      canvas.style.height = "auto";

      const logoSvg = useLogo ? await loadLogoSvg() : "";
      if (logoSvg) {
        const ctx = canvas.getContext("2d");
        const logo = new Image();
        const sized = logoSvg.replace(
          "<svg ",
          `<svg width="${LOGO_W}" height="${LOGO_H}" `
        );
        logo.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(sized)}`;
        await new Promise((resolve) => {
          logo.onload = resolve;
          logo.onerror = resolve;
        });
        const w = canvas.width * LOGO_SCALE;
        const h = w * (LOGO_H / LOGO_W);
        const x = (canvas.width - w) / 2;
        const y = (canvas.height - h) / 2;
        const pad = canvas.width * 0.025;
        ctx.fillStyle = bg;
        ctx.fillRect(x - pad, y - pad, w + pad * 2, h + pad * 2);
        ctx.drawImage(logo, x, y, w, h);
      }

      const png = canvas.toDataURL("image/png");
      let svg = await QRCode.toString(v, {
        type: "svg",
        margin: 2,
        errorCorrectionLevel: "H",
        color: { dark: fg, light: bg },
      });

      if (logoSvg) {
        const n = parseFloat(svg.match(/viewBox="0 0 (\d+)/)?.[1]);
        const inner = logoSvg.match(/<g id="Logo">([\s\S]*)<\/g>/)?.[1];
        if (n && inner) {
          const k = (n * LOGO_SCALE) / LOGO_W;
          const w = LOGO_W * k;
          const h = LOGO_H * k;
          const x = (n - w) / 2;
          const y = (n - h) / 2;
          const pad = n * 0.025;
          const logoG =
            `<rect x="${x - pad}" y="${y - pad}" width="${w + pad * 2}" height="${h + pad * 2}" fill="${bg}"/>` +
            `<g transform="translate(${x} ${y}) scale(${k})">${inner.replace(/class="cls-1"/g, "")}</g>`;
          svg = svg.replace("</svg>", `${logoG}</svg>`);
        }
      }

      setQrPng(png);
      setQrSvg(svg);
      requestAnimationFrame(playReveal);
    } catch (e) {
      setErrMsg(t("Couldn't generate that QR code, try a shorter link"));
      setQrPng("");
      setQrSvg("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (profileUrl) generate(profileUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validates the fields for the selected type and returns the string to encode.
  const getPayload = () => {
    if (qrType === "wifi") {
      if (!ssid.trim()) return { error: t("Enter the network name") };
      if (security !== "nopass" && !wifiPassword) {
        return { error: t("Enter the Wi-Fi password") };
      }
      return {
        payload: buildWifiPayload({
          ssid,
          password: wifiPassword,
          security,
          hidden: hiddenNet,
        }),
      };
    }
    return { payload: text };
  };

  const submit = () => {
    const { payload, error } = getPayload();
    if (error) {
      setErrMsg(error);
      setQrPng("");
      setQrSvg("");
      return;
    }
    generate(payload);
  };

  const changeType = (type) => {
    if (type === qrType) return;
    setQrType(type);
    setErrMsg("");
    setQrPng("");
    setQrSvg("");
  };

  // Toggling the logo re-renders the code straight away.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (qrPng) submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useLogo]);

  const onSubmit = (e) => {
    e.preventDefault();
    submit();
  };

  const downloadSvg = () => {
    const blob = new Blob([qrSvg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "linkakam-qr.svg";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <main className="bg-white min-h-[70vh]">
      <div className="max-w-8xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <p className="text-sm font-semibold uppercase tracking-wide text-brand-600">
          {t("Tool")}
        </p>
        <h1 className="mt-2 font-display font-extrabold text-3xl text-ink-900">
          {t("Generate your QR code")}
        </h1>
        <p className="mt-3 text-ink-500 max-w-xl">
          {t("Turn your Linkakam profile or any link into a QR code you can print, share and download.")}
        </p>

        <div className="mt-10 grid md:grid-cols-2 gap-10 items-start">
          <form onSubmit={onSubmit} className="space-y-6">
            <fieldset>
              <legend className="block text-sm font-medium text-ink-700 mb-2">
                {t("QR code type")}
              </legend>
              <div className="flex flex-wrap gap-3">
                {QR_TYPES.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => changeType(o.id)}
                    aria-pressed={qrType === o.id}
                    className={`h-11 px-5 rounded-full text-sm font-semibold border transition-colors ${
                      qrType === o.id
                        ? "bg-ink-900 text-white border-ink-900"
                        : "border-ink-200 text-ink-700 hover:border-brand-500 hover:text-brand-600"
                    }`}
                  >
                    {t(o.label)}
                  </button>
                ))}
              </div>
            </fieldset>

            {qrType === "link" ? (
              <div>
                <label htmlFor="qr-text" className="block text-sm font-medium text-ink-700 mb-1.5">
                  {t("Link or text")}
                </label>
                <input
                  id="qr-text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="https://linkakam.com/yourname"
                  className={INPUT_CLASS}
                />
                {profileUrl && text !== profileUrl && (
                  <button
                    type="button"
                    onClick={() => setText(profileUrl)}
                    className="mt-2 text-xs font-semibold text-brand-600 hover:underline"
                  >
                    {t("Use my profile link")}
                  </button>
                )}
              </div>
            ) : (
              <>
                <div>
                  <label htmlFor="wifi-ssid" className="block text-sm font-medium text-ink-700 mb-1.5">
                    {t("Network name (SSID)")}
                  </label>
                  <input
                    id="wifi-ssid"
                    value={ssid}
                    onChange={(e) => setSsid(e.target.value)}
                    autoComplete="off"
                    className={INPUT_CLASS}
                  />
                </div>

                <div>
                  <label htmlFor="wifi-security" className="block text-sm font-medium text-ink-700 mb-1.5">
                    {t("Security")}
                  </label>
                  <select
                    id="wifi-security"
                    value={security}
                    onChange={(e) => setSecurity(e.target.value)}
                    className={INPUT_CLASS}
                  >
                    {WIFI_SECURITY.map((o) => (
                      <option key={o.value} value={o.value}>
                        {t(o.label)}
                      </option>
                    ))}
                  </select>
                </div>

                {security !== "nopass" && (
                  <div>
                    <label htmlFor="wifi-password" className="block text-sm font-medium text-ink-700 mb-1.5">
                      {t("Password")}
                    </label>
                    <div className="relative">
                      <input
                        id="wifi-password"
                        type={showPassword ? "text" : "password"}
                        value={wifiPassword}
                        onChange={(e) => setWifiPassword(e.target.value)}
                        autoComplete="off"
                        className={`${INPUT_CLASS} pe-20`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-pressed={showPassword}
                        className="absolute inset-y-0 end-4 text-xs font-semibold text-brand-600 hover:underline"
                      >
                        {showPassword ? t("Hide") : t("Show")}
                      </button>
                    </div>
                  </div>
                )}

                <label className="flex items-center gap-3 text-sm text-ink-700">
                  <input
                    type="checkbox"
                    checked={hiddenNet}
                    onChange={(e) => setHiddenNet(e.target.checked)}
                    className="w-5 h-5 rounded-md border-ink-300 text-brand-600 focus:ring-brand-500"
                  />
                  {t("Hidden network")}
                </label>
              </>
            )}

            <fieldset>
              <legend className="block text-sm font-medium text-ink-700 mb-2">
                {t("Foreground color")}
              </legend>
              <div className="flex flex-wrap gap-3">
                {FG_SWATCHES.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setFg(s.value)}
                    aria-label={t(s.name)}
                    aria-pressed={fg === s.value}
                    className={`w-11 h-11 rounded-full border-2 transition-transform ${
                      fg === s.value ? "border-ink-900 scale-110" : "border-transparent"
                    }`}
                    style={{ backgroundColor: s.value }}
                  />
                ))}
              </div>
            </fieldset>

            <fieldset>
              <legend className="block text-sm font-medium text-ink-700 mb-2">
                {t("Background color")}
              </legend>
              <div className="flex flex-wrap gap-3">
                {BG_SWATCHES.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setBg(s.value)}
                    aria-label={t(s.name)}
                    aria-pressed={bg === s.value}
                    className={`w-11 h-11 rounded-full border-2 transition-transform ${
                      bg === s.value ? "border-ink-900 scale-110" : "border-ink-200"
                    }`}
                    style={{ backgroundColor: s.value }}
                  />
                ))}
              </div>
            </fieldset>

            <label className="flex items-center gap-3 text-sm text-ink-700">
              <input
                type="checkbox"
                checked={useLogo}
                onChange={(e) => setUseLogo(e.target.checked)}
                className="w-5 h-5 rounded-md border-ink-300 text-brand-600 focus:ring-brand-500"
              />
              {t("Add Linkakam logo to the center")}
            </label>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={loading}
                className="h-11 px-6 rounded-full bg-brand-600 text-white font-semibold
                  hover:bg-brand-700 active:scale-[0.97] transition-[background-color,transform] duration-150
                  disabled:opacity-60 disabled:cursor-not-allowed
                  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
              >
                {loading ? t("Generating...") : t("Generate")}
              </button>
            </div>

            <div role="status" aria-live="polite">
              {errMsg && <p className="text-sm text-red-600">{errMsg}</p>}
            </div>
          </form>

          <div className="flex flex-col items-center gap-4">
            <div
              ref={revealRef}
              className="relative overflow-hidden rounded-[2rem] border border-ink-100 shadow-soft p-6 bg-white"
              style={{ opacity: qrPng ? undefined : 1 }}
            >
              <canvas
                ref={canvasRef}
                className="block w-64 aspect-square max-w-full rounded-3xl"
              />
              {!qrPng && (
                <p className="mt-3 text-xs text-center text-ink-400 max-w-[16rem]">
                  {t("Your QR code will appear here.")}
                </p>
              )}
              <div
                ref={scanRef}
                aria-hidden="true"
                className="pointer-events-none absolute inset-6 rounded-3xl opacity-0"
                style={{
                  background:
                    "linear-gradient(100deg, transparent 40%, rgba(42,92,230,0.35) 50%, transparent 60%)",
                }}
              />
            </div>

            {qrPng && (
              <div className="flex gap-3">
                <a
                  href={qrPng}
                  download="linkakam-qr.png"
                  className="h-11 px-5 rounded-full bg-ink-900 text-white text-sm font-semibold flex items-center
                    hover:bg-ink-800 transition-colors
                    focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-900"
                >
                  {t("Download PNG")}
                </a>
                <button
                  type="button"
                  onClick={downloadSvg}
                  className="h-11 px-5 rounded-full border border-ink-200 text-sm font-semibold text-ink-700
                    hover:border-brand-500 hover:text-brand-600 transition-colors"
                >
                  {t("Download SVG")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
